import { useCallback, useEffect, useState } from "react";
import { DoorOpen, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const time = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");

export const StationBoard = ({ eventId, className = "" }) => {
  const [board, setBoard] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/stations`);
      setBoard(data);
    } catch {
      /* the board is informational; a blip should not break the check-in screen */
    }
  }, [eventId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const busiest = board?.stations?.reduce(
    (top, s) => (s.last_15_min > (top?.last_15_min || 0) ? s : top),
    null
  );

  return (
    <div className={`card p-5 ${className}`} data-testid="station-board">
      <div className="flex items-center justify-between gap-3">
        <p className="label mb-0 flex items-center gap-2">
          <DoorOpen size={14} className="text-[#00e599]" /> Stations
        </p>
        <div className="flex items-center gap-3">
          <span className="font-mono2 text-[11px] text-slate-500" data-testid="station-board-total">
            {board?.total ?? 0} in · {board?.last_15_min ?? 0} last 15 min
          </span>
          <button onClick={load} className="btn-ghost text-xs" data-testid="station-board-refresh">
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {!board?.stations?.length ? (
        <p className="text-sm text-slate-500 mt-4" data-testid="station-board-empty">
          No station activity yet.
        </p>
      ) : (
        <div className="mt-4 space-y-3.5">
          {board.stations.map((s) => (
            <div key={s.station_id} data-testid={`station-row-${s.station_id}`}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-slate-100 truncate">
                  {s.station_id}
                  {busiest && busiest.last_15_min > 0 && busiest.station_id === s.station_id && (
                    <span className="font-mono2 text-[10px] text-[#ffb800] ml-2" data-testid="station-busiest">
                      busiest now
                    </span>
                  )}
                </span>
                <span className="font-mono2 text-[11px] text-slate-500 shrink-0">
                  <span className="text-[#00e599]">{s.count}</span> · {s.share}% · last {time(s.last_at)}
                </span>
              </div>
              <div className="h-1.5 mt-1.5 rounded-full bg-[#1c2431] overflow-hidden">
                <div
                  className="h-full bg-[#00e599] transition-all duration-500"
                  style={{ width: `${s.share}%` }}
                />
              </div>
              <p className="font-mono2 text-[10px] text-slate-600 mt-1">
                {s.last_15_min} in the last 15 min
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
